import { observer } from 'mobx-react-lite'

import { areRevisionsEnabled } from '#choo-app/lib/editor/plugins/revisions'
import { toggleLineRevision } from '#choo-app/lib/editor/plugins/revisions-commands'
import { ChangeTrackingMenuButton } from '#components/ChangeTracking/MenuButton'
import { ScriptTimingPopover } from '#components/EditorToolbar/TimingPopover/ScriptTimingPopover'
import { ReadonlyScriptBanner } from '#components/ReadOnlyBanner'
import { Toolbar } from '#components/Toolbar'
import { ToolbarEditorButton } from '#components/Toolbar/ToolbarEditorButton'
import { useDebouncedElementSize } from '#hooks'
import { InkExportMenu } from '#ink/components'
import { useMst } from '#state'
import { ButtonMode, ILoadedScript, IOrg } from '#state/types'
import { isInteger } from '#util'

import { CommentHistoryButton } from '../Comments/CommentHistoryButton'

import { CreateSnapshotButton } from './buttons/CreateSnapshot'
import { ExcludeFromTimingButton } from './buttons/ExcludeFromTiming'
import { OpenSnapshotSidebar } from './buttons/OpenSnapshotSidebar'
import { Collaborators } from './Collaborators'
import { ElementMenu } from './ElementMenu'
import { ExtrasMenu } from './ExtrasMenu/ExtrasMenu'
import { FormattingOptions } from './Formatting'
import { NewCommentButton } from './Formatting/NewComment'
import { ProductionMenu } from './ProductionMenu'
import { getItemsToShrink, ToolbarItem } from './responsiveHelpers'

import styles from './EditorToolbar.module.scss'

export const EditorToolbar = observer(function EditorToolbar({
  script,
  org,
  toggleChangeTrackingPanel,
  toggleSnapshotsPanel,
  openRevisionsModal,
}: {
  script: ILoadedScript
  org: IOrg
  toggleChangeTrackingPanel: () => void
  toggleSnapshotsPanel: () => void
  openRevisionsModal: () => void
}) {
  const { currentInkProject, view } = useMst()
  const { ref, width } = useDebouncedElementSize()

  const {
    collaborators,
    hasRemoteUsers,
    readOnlyByPlan,
    isEditable,
    isInk,
    isPrivate,
    isScreenplay,
    observableEditor,
  } = script

  // plan-restricted scripts keep their editing tools, shown disabled
  const showEditingTools = isEditable || !!readOnlyByPlan

  // Simplified toolbar when editing title page
  const itemsToRender: ToolbarItem[] = script.titlePage.titlePageVisible
    ? ['FORMATTING_BUTTONS', 'EXTRAS_MENU']
    : ['SNAPSHOT_BUTTON', 'COMMENT_MENU', 'SNAPSHOT_SIDEBAR_BUTTON']

  if (!script.titlePage.titlePageVisible) {
    if (!isInk && !isScreenplay) {
      itemsToRender.push('TIMING_POPOVER_BUTTON')
      if (showEditingTools) {
        itemsToRender.push('EXCLUDE_FROM_TIMING_BUTTON')
      }
    }
    if (showEditingTools) {
      itemsToRender.push('FORMATTING_BUTTONS')
      if (!isInk) itemsToRender.push('ELEMENT_MENU')
    } else {
      itemsToRender.push('NEW_COMMENT_BUTTON') // bundled w/ formatting buttons in editable scripts
    }
    if (isInk) {
      itemsToRender.push('INK_EXPORT_MENU')
      itemsToRender.push('INK_PLAYER')
    } else {
      itemsToRender.push('EXTRAS_MENU')
      itemsToRender.push('PRODUCTION_MENU')
    }
    const showChangeTracking =
      org.hasChangeTrackingEnabled && !isInk && !isPrivate

    if (showChangeTracking) {
      itemsToRender.push('CHANGE_TRACKING')
    }
  }

  if (hasRemoteUsers) {
    itemsToRender.push('COLLABORATORS')
  }

  const { mini, micro } = getItemsToShrink(itemsToRender, width)
  const getModeFor = (item: ToolbarItem): ButtonMode =>
    micro.includes(item) ? 'micro' : mini.includes(item) ? 'mini' : 'normal'

  if (script.inTrash) {
    return <ReadonlyScriptBanner script={script} />
  }

  const revisionsEnabled =
    script.pmEditor.editorState &&
    areRevisionsEnabled(script.pmEditor.editorState)

  return (
    <div className={styles.toolbar} ref={ref}>
      <Toolbar>
        <Toolbar.Section position="left">
          {!script.titlePage.titlePageVisible && (
            <Toolbar.ButtonGroup spacing={4}>
              {itemsToRender.includes('INK_PLAYER') && (
                <Toolbar.Button
                  tooltip="Show Player Window"
                  label="Play"
                  icon="fa-play"
                  onClick={currentInkProject?.launchPopup}
                  primary
                />
              )}
              {itemsToRender.includes('ELEMENT_MENU') && observableEditor && (
                <ElementMenu
                  script={script}
                  observableEditor={observableEditor}
                  mode={getModeFor('ELEMENT_MENU')}
                />
              )}
              {itemsToRender.includes('TIMING_POPOVER_BUTTON') && (
                <>
                  <ScriptTimingPopover
                    script={script}
                    mode={getModeFor('TIMING_POPOVER_BUTTON')}
                    height={view.dimensions.scriptScroller.height}
                  />
                  {itemsToRender.includes('EXCLUDE_FROM_TIMING_BUTTON') &&
                    observableEditor && (
                      <ExcludeFromTimingButton
                        observableEditor={observableEditor}
                      />
                    )}
                </>
              )}
            </Toolbar.ButtonGroup>
          )}
          {itemsToRender.includes('NEW_COMMENT_BUTTON') && <NewCommentButton />}
          {itemsToRender.includes('FORMATTING_BUTTONS') && (
            <FormattingOptions mode={getModeFor('FORMATTING_BUTTONS')} />
          )}
          {revisionsEnabled && (
            <ToolbarEditorButton
              command={toggleLineRevision}
              icon="fa-asterisk"
              tooltip="Toggle line revision"
            />
          )}
          {!script.titlePage.titlePageVisible && (
            <>
              <Toolbar.Divider />
              <CreateSnapshotButton
                script={script}
                mode={getModeFor('SNAPSHOT_BUTTON')}
              />
            </>
          )}
        </Toolbar.Section>
        <Toolbar.Section position="right">
          {itemsToRender.includes('COLLABORATORS') && (
            <Collaborators
              collaborators={collaborators}
              mode={getModeFor('COLLABORATORS')}
            />
          )}
          {isInteger(script.totalCommentCount) && (
            <CommentHistoryButton
              script={script}
              mode={getModeFor('COMMENT_MENU')}
              count={script.totalCommentCount ?? 0}
              disableSlack={org.isUnpaid}
            />
          )}
          {itemsToRender.includes('CHANGE_TRACKING') && (
            <ChangeTrackingMenuButton
              togglePanel={toggleChangeTrackingPanel}
              mode={getModeFor('CHANGE_TRACKING')}
            />
          )}
          {!script.titlePage.titlePageVisible && (
            <OpenSnapshotSidebar
              script={script}
              mode={getModeFor('SNAPSHOT_SIDEBAR_BUTTON')}
              toggleSnapshotsPanel={toggleSnapshotsPanel}
            />
          )}
          {itemsToRender.includes('PRODUCTION_MENU') && (
            <ProductionMenu
              mode={getModeFor('PRODUCTION_MENU')}
              openRevisionsModal={openRevisionsModal}
            />
          )}
          {itemsToRender.includes('EXTRAS_MENU') && <ExtrasMenu />}
          {itemsToRender.includes('INK_EXPORT_MENU') && (
            <InkExportMenu mode={getModeFor('INK_EXPORT_MENU')} />
          )}
        </Toolbar.Section>
      </Toolbar>
      <ReadonlyScriptBanner script={script} />
    </div>
  )
})
